
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { PixelFormValues } from "./CreatePixelForm";
import { Loader2 } from "lucide-react";
import { isValidInput, validateUserInput } from "@/utils/security";
import { toast } from "sonner";

interface EditPixelFormProps {
  pixel: {
    id: string;
    name: string;
    domain: string;
  };
  onSubmit: (data: PixelFormValues) => Promise<void>;
  isSubmitting?: boolean;
}

export function EditPixelForm({ pixel, onSubmit, isSubmitting = false }: EditPixelFormProps) {
  const form = useForm<PixelFormValues>({
    defaultValues: {
      name: pixel.name,
      domain: pixel.domain,
    },
  });

  // Handle input validation with notifications
  const handleInputChange = (field: keyof PixelFormValues, value: string) => {
    const validation = validateUserInput(value);
    if (!validation.isValid && validation.message) {
      toast.error(validation.message);
      return false;
    }
    return true;
  };

  const validateForm = async (data: PixelFormValues) => {
    const errors: Record<string, string> = {};
    
    // Validate pixel name with notification
    const nameValidation = validateUserInput(data.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.message || "Pixel name contains invalid characters";
      form.setError("name", { type: "manual", message: errors.name });
      toast.error(errors.name);
      return false;
    }
    
    // Validate pixel name - should not contain script tags or other dangerous content
    if (!isValidInput(data.name)) {
      errors.name = "Pixel name contains invalid characters";
      form.setError("name", { type: "manual", message: errors.name });
      toast.error(errors.name);
      return false;
    }
    
    // Domain validation - basic format check
    const domainRegex = /^(https?:\/\/)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](\.[a-zA-Z]{2,})+\/?$/;
    if (!domainRegex.test(data.domain)) {
      errors.domain = "Please enter a valid domain (e.g., example.com)";
      form.setError("domain", { type: "manual", message: errors.domain });
      toast.error(errors.domain);
      return false;
    }
    
    // Domain security check
    const domainValidation = validateUserInput(data.domain);
    if (!domainValidation.isValid) {
      errors.domain = domainValidation.message || "Domain contains invalid characters";
      form.setError("domain", { type: "manual", message: errors.domain });
      toast.error(errors.domain);
      return false;
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (data: PixelFormValues) => {
    const isValid = await validateForm(data);
    if (isValid) {
      await onSubmit(data);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          rules={{ 
            required: "Pixel name is required",
            validate: (value) => isValidInput(value) || "Pixel name contains invalid characters" 
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pixel Name</FormLabel>
              <FormControl>
                <Input 
                  placeholder="My Website" 
                  {...field} 
                  onChange={(e) => {
                    const isValid = handleInputChange('name', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="domain"
          rules={{ 
            required: "Domain is required",
            pattern: {
              value: /^(https?:\/\/)?[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](\.[a-zA-Z]{2,})+\/?$/,
              message: "Please enter a valid domain (e.g., example.com or app.example.com)"
            }
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Domain</FormLabel>
              <FormControl>
                <Input 
                  placeholder="example.com or app.example.com" 
                  {...field} 
                  onChange={(e) => {
                    const isValid = handleInputChange('domain', e.target.value);
                    if (isValid) field.onChange(e);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter className="flex justify-center sm:justify-center">
          <Button 
            type="submit" 
            className="bg-black hover:bg-black/90 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
